import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
  getUserRole,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import { canAccessRoute } from 'dashboard/constants/sidebarPermissions';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

const AGENT_ALLOWED_ROUTES = [
  { permissions: [...ROLES, ...CONVERSATION_PERMISSIONS], path: 'inbox-view' },
  { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
  { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
  { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
];

const DEFAULT_PERMISSION_ROUTES = [
  { permissions: [...ROLES, ...CONVERSATION_PERMISSIONS], path: 'dashboard' },
  ...AGENT_ALLOWED_ROUTES,
];

export const defaultRedirectPage = (to, permissions, userRole = null) => {
  const { accountId } = to.params;
  const routes =
    userRole === 'agent' ? AGENT_ALLOWED_ROUTES : DEFAULT_PERMISSION_ROUTES;

  const route = routes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  const defaultPath = route ? route.path : userRole === 'agent' ? 'inbox-view' : 'dashboard';
  return `accounts/${accountId}/${defaultPath}`;
};

const validateActiveAccountRoutes = (to, user) => {
  const accountId = to.params.accountId;
  const accountDashboardURL = `accounts/${accountId}/dashboard`;

  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userRole = getUserRole(user, accountId);
  const userPermissions = getUserPermissions(user, accountId);

  if (!canAccessRoute(to.name, userRole)) {
    return defaultRedirectPage(to, userPermissions, userRole);
  }

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  return isAccessible ? null : defaultRedirectPage(to, userPermissions, userRole);
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
